import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const HowItWorks = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          pt: { xs: "100px", sm: "120px", md: "140px" },
          pb: { xs: "40px", sm: "60px", md: "80px" },
          px: { xs: 2, sm: 4, md: 0 },
          color: "white",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 50%)",
          },
        }}
      >
        {/* Animated Bubbles */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: 100, opacity: 0 }}
            animate={{
              y: [-20, 20],
              opacity: [0.3, 0.5, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))",
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
              pointerEvents: "none",
            }}
          />
        ))}
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                    fontWeight: 800,
                    mb: { xs: 2, md: 3 },
                    textShadow: "2px 4px 8px rgba(0,0,0,0.2)",
                    background: "linear-gradient(to right, #ffffff, #f0f0f0)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "0.02em",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-10px",
                      left: "0",
                      width: "60px",
                      height: "3px",
                      background: "white",
                      borderRadius: "2px",
                    },
                  }}
                >
                  Your Vehicle Donation Process is Quick & Easy
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    transform: "perspective(1000px) rotateY(-5deg)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "perspective(1000px) rotateY(0deg)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/7.jpeg"
                    alt="Vehicle Donation Process"
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      transform: "scale(1.05)",
                    }}
                  />
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Steps Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={4}>
          {[
            {
              icon: <NotificationsActiveIcon sx={{ fontSize: 40 }} />,
              title: "1. Notify Us",
              description:
                "Click the \"Donate Now\" Button, Call or Chat Live with a VFYV team member to schedule a free vehicle pickup at your earliest convenience. Call: 1(833) 838-8398",
            },
            {
              icon: <img 
                src="/images/towing.png" 
                alt="Pickup Icon" 
                style={{ 
                  width: '60px', 
                  height: '60px',
                  filter: 'brightness(0) invert(1)' // This will make the image white
                }} 
              />,
              title: "2. Quick Pick Up",
              description:
                "Your vehicle will be picked up quickly at the date and time that is convenient for you. (You don't need to be present)",
            },
            {
              icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
              title: "3. Receive Your Deduction",
              description:
                "After your vehicle has been picked up you will receive an IRS tax donation receipt.",
            },
          ].map((step, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: { xs: 3, sm: 4 },
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.8)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid",
                    borderColor: "grey.200",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                    display: 'flex',
                    flexDirection: 'column',
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    },
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      mx: "auto",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: "white",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {step.title}
                  </Typography>
                  {index === 0 ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Click the &quot;Donate Now&quot; Button, Call or Chat Live with a VFYV team member to schedule a free vehicle pickup at your earliest convenience.
                      </Typography>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                          Call:
                        </Typography>
                        <Box 
                          component="a" 
                          href="tel:+18338388398" 
                          sx={{ 
                            color: 'primary.main', 
                            textDecoration: 'none', 
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            '&:hover': { textDecoration: 'underline' } 
                          }}
                        >
                          1(833) 838-8398
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, flex: 1 }}
                    >
                      {step.description}
                    </Typography>
                  )}
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Process Description Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,245,245,0.95) 100%)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 3,
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-12px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "80px",
                  height: "4px",
                  background: theme.palette.primary.main,
                  borderRadius: "2px",
                },
              }}
            >
              Easy Donation Process
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                mb: 6,
                maxWidth: "800px",
                mx: "auto",
                color: "text.secondary",
                lineHeight: 1.8,
                letterSpacing: "0.02em",
              }}
            >
              Easy, hassle-free process, simply fill out Donation Form with
              basic contact and vehicle details and have your vehicle picked up
              for FREE in as fast as 24-48 hours or less!
            </Typography>

            {/* Request Donation Pickup Section */}
            <Box sx={{ mt: 8 }}>
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: 4,
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                Request Your Donation Pick Up
              </Typography>
              <Card
                elevation={2}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 2,
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  maxWidth: "900px",
                  mx: "auto",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle at 0% 0%, rgba(0,40,104,0.03) 0%, rgba(255,255,255,0) 50%)",
                  },
                }}
              >
                <Stack spacing={3}>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ color: "text.secondary", lineHeight: 1.8 }}
                  >
                    Complete the secure online Donation Form, please include your contact information,
                    vehicle location and some general information about the vehicle. Include as many details
                    about your vehicle that apply, use the “Mechanical Notes” box to add any other details
                    about your donation, you can even add images to help maximize the remarketing value
                    of your donated vehicle.
                  </Typography>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ color: "text.secondary", lineHeight: 1.8 }}
                  >
                    Our process is simple, hassle-free, and at your convenience.
                    All we need is the vehicle pickup location, keys and vehicle
                    title, we will take care of everything from start to finish.
                  </Typography>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      mt: 2,
                    }}
                  >
                    Vehicles For Young Veterans representatives are available to
                    answer any questions you may have. We appreciate your
                    support in helping our veterans!
                  </Typography>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ color: "text.secondary", lineHeight: 1.8 }}
                  >
                    We also accept all types of vehicles, trucks, atv&apos;s,
                    boats, RV&apos;s, and even properties. If you are interested
                    in donating any of these items please CONTACT US.
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 6 }}
                  >
                    <Button
                      component={Link}
                      to="/contact"
                      variant="contained"
                      color="secondary"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: "0 4px 14px 0 rgba(0,0,0,0.2)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px 0 rgba(0,0,0,0.2)",
                        },
                      }}
                    >
                      Contact us
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Detailed Steps Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Schedule Pickup Section */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card
                  elevation={2}
                  sx={{
                    p: 4,
                    minHeight: '420px',
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.9)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                    }}
                  >
                    Schedule Your FREE Vehicle Pick Up
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                    Once you submit your vehicle donation request, one of our professional transportation
                    companies will contact you to schedule a convenient date and time to pick up your vehicle. It will
                    usually be within 24 hours.
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                    Vehicle pick up is FREE and you do not have to be present, just
                    make sure you&apos;ve removed the license plate and any personal items from the vehicle. If you
                    need assistance with license plate removal, they will be happy to assist you.
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      mt: 2,
                      flex: 1 
                    }}
                  >
                    You will receive a vehicle donation pick up slip from Vehicles For Young Veterans for your records.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            {/* Tax Deduction Section */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card
                  elevation={2}
                  sx={{
                    p: 4,
                    minHeight: '420px',
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.9)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                    }}
                  >
                    Receive Your Tax Deduction
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                    Once your donated vehicle has been picked up and received along with the keys and
                    title, we will mail or email your IRS tax-deductible donation receipt in approximately 10-
                    15 business days.
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                    Making a real difference just got a lot easier! Together we can make your generous
                    contribution directly impact the lives of veterans today, benefiting our Veteran
                    Community in their time of need.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/donate"
                    sx={{
                      mt: 3,
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      borderRadius: '50px',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                      }
                    }}
                  >
                    Donate Now
                  </Button>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Veterans Support Image Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: theme.palette.grey[50] }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Making a Difference Together
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
                  Your vehicle donation helps us provide essential support and services to Young
                  Veterans, Single Parent Veteran Families and all those who have served our nation. By
                  donating your vehicle, you&#39;re directly contributing to programs that assist veterans in
                  their time of need.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  From providing health services to offering financial assistance, your contribution makes
                  a meaningful impact benefiting our Veteran Community.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    transform: 'perspective(1000px) rotateY(5deg)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'perspective(1000px) rotateY(0deg) scale(1.02)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/IMG_9728.jpeg"
                    alt="Veterans Support"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HowItWorks;
